import { useEffect, useMemo, useRef, useState } from 'react'
import { autocompleteService, AutocompleteSuggestion, NoteSectionType } from '../services/autocompleteService'
import { customPhraseService, CreateCustomPhraseRequest } from '../services/customPhraseService'
import { TerminologySearchableSystem } from '../services/terminologyService'
import { NoteHighlight } from '../types/consultation'
import './ConsultationNoteEditor.css'

interface ConsultationNoteEditorProps {
  value: string
  onChange: (value: string) => void
  onHighlightsChange?: (highlights: NoteHighlight[]) => void
  highlights?: NoteHighlight[] // Existing highlights from saved notes
  sectionType?: NoteSectionType
  terminologySystem: TerminologySearchableSystem
  reasonForVisit?: string
  diagnosis?: string[]
}

interface InlineState {
  active: boolean
  suggestion?: AutocompleteSuggestion
}

const DEBOUNCE_MS = 250

export function ConsultationNoteEditor(props: ConsultationNoteEditorProps) {
  const { value, onChange, onHighlightsChange, highlights: initialHighlights = [], sectionType = 'notes', terminologySystem, reasonForVisit, diagnosis } = props
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [inlineState, setInlineState] = useState<InlineState>({ active: false })
  const [isLoading, setIsLoading] = useState(false)
  const [caretPosition, setCaretPosition] = useState(0)
  const [selectedText, setSelectedText] = useState<string>('')
  const [showSavePhraseModal, setShowSavePhraseModal] = useState(false)
  const [savePhraseTrigger, setSavePhraseTrigger] = useState('')
  const debounceRef = useRef<number | undefined>(undefined)
  const selectionPopupRef = useRef<HTMLDivElement | null>(null)
  const [highlights, setHighlights] = useState<NoteHighlight[]>(initialHighlights)
  const previousValueRef = useRef<string>(value)
  const justAppliedSuggestionRef = useRef<boolean>(false)

  // Update caret position when textarea selection changes
  const handleSelectionChange = () => {
    const el = textareaRef.current
    if (el) {
      setCaretPosition(el.selectionStart ?? value.length)
      
      // Check for text selection
      const selectionStart = el.selectionStart
      const selectionEnd = el.selectionEnd
      if (selectionStart !== null && selectionEnd !== null && selectionStart !== selectionEnd) {
        const selected = value.slice(selectionStart, selectionEnd).trim()
        if (selected.length > 0) {
          setSelectedText(selected)
          // Position popup near selection (simplified - could be more sophisticated)
          updateSelectionPopupPosition(el, selectionStart, selectionEnd)
        } else {
          setSelectedText('')
        }
      } else {
        setSelectedText('')
      }
    }
  }

  // Position the selection popup near the selected text
  const updateSelectionPopupPosition = (_el: HTMLTextAreaElement, start: number, _end: number) => {
    if (!selectionPopupRef.current) return
    
    // Calculate approximate position based on textarea scroll and selection
    // This is a simplified approach - a more sophisticated version would
    // calculate exact pixel positions of the selected text
    const textBeforeSelection = value.slice(0, start)
    const lines = textBeforeSelection.split('\n')
    const lineNumber = lines.length - 1
    const lineHeight = 20 // Approximate line height in pixels
    const padding = 8
    
    // Position popup above the selection (or below if near top)
    const topOffset = Math.max(lineNumber * lineHeight - 30, 10)
    selectionPopupRef.current.style.top = `${topOffset}px`
    selectionPopupRef.current.style.left = `${padding}px`
  }

  const handleSaveAsPhrase = () => {
    if (!selectedText.trim()) return
    setSavePhraseTrigger(selectedText.slice(0, 30).trim()) // Use first 30 chars as default trigger
    setShowSavePhraseModal(true)
  }

  const handleSavePhrase = async (trigger: string) => {
    if (!selectedText.trim() || !trigger.trim()) return

    try {
      const phraseData: CreateCustomPhraseRequest = {
        text: selectedText,
        trigger: trigger.trim(),
        sectionType,
        category: 'custom',
      }

      console.log('[ConsultationNoteEditor] Saving custom phrase:', { trigger: phraseData.trigger, textLength: phraseData.text.length, sectionType: phraseData.sectionType })
      
      const saved = await customPhraseService.create(phraseData)
      console.log('[ConsultationNoteEditor] Custom phrase saved successfully:', saved)
      
      // Clear selection and close modal
      setSelectedText('')
      setShowSavePhraseModal(false)
      
      // Clear textarea selection
      const el = textareaRef.current
      if (el) {
        el.setSelectionRange(el.selectionStart, el.selectionStart)
      }
    } catch (error) {
      console.error('[ConsultationNoteEditor] Failed to save custom phrase:', error)
      alert('Failed to save phrase. Please try again.')
    }
  }

  const handleDeleteCustomPhrase = async (phraseId: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering the suggestion selection
    e.preventDefault()
    
    if (!confirm('Are you sure you want to delete this custom phrase? This action cannot be undone.')) {
      return
    }

    try {
      await customPhraseService.delete(phraseId)
      console.log('[ConsultationNoteEditor] Custom phrase deleted:', phraseId)
      
      // Remove the suggestion from the list
      setSuggestions(prev => prev.filter(s => s.custom_phrase_id !== phraseId))
    } catch (error) {
      console.error('[ConsultationNoteEditor] Failed to delete custom phrase:', error)
      alert('Failed to delete phrase. Please try again.')
    }
  }

  const { textBeforeCursor, textAfterCursor, typedPrefix } = useMemo(() => {
    const pos = caretPosition
    const before = value.slice(0, pos)
    const after = value.slice(pos)
    const match = before.match(/([^\s\n\r\t]*)$/)
    const prefix = match ? match[1] : ''
    return {
      textBeforeCursor: before,
      textAfterCursor: after,
      typedPrefix: prefix,
    }
  }, [value, caretPosition])

  // Allow autocomplete for single character if it's a common prefix (like "no")
  // But prefer 2+ characters for better results
  const canTrigger = typedPrefix.length >= 1

  useEffect(() => {
    // Don't trigger autocomplete immediately after applying a suggestion
    if (justAppliedSuggestionRef.current) {
      justAppliedSuggestionRef.current = false
      return
    }

    if (!canTrigger) {
      setSuggestions([])
      setInlineState({ active: false, suggestion: undefined })
      return
    }

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current)
    }

    debounceRef.current = window.setTimeout(async () => {
      try {
        setIsLoading(true)
        console.log('[ConsultationNoteEditor] Requesting autocomplete:', {
          sectionType,
          typedPrefix,
          textBeforeCursor: textBeforeCursor.slice(-20), // Last 20 chars for debugging
          terminologySystem,
        })
        
        const results = await autocompleteService.autocomplete({
          sectionType,
          textBeforeCursor,
          textAfterCursor,
          typedPrefix,
          cursorPosition: caretPosition,
          encounter: {
            reasonForVisit,
            problems: diagnosis,
          },
          terminologySystem,
        })

        console.log('[ConsultationNoteEditor] Autocomplete results:', results)
        setSuggestions(results)
        setSelectedIndex(0)

        const firstTerminology = results.find((s) => s.type === 'terminology')
        if (firstTerminology && firstTerminology.preview_text.toLowerCase().startsWith(typedPrefix.toLowerCase())) {
          setInlineState({
            active: true,
            suggestion: firstTerminology,
          })
        } else {
          setInlineState({ active: false, suggestion: undefined })
        }
      } catch (error) {
        console.error('[ConsultationNoteEditor] Autocomplete error', error)
        setSuggestions([])
        setInlineState({ active: false, suggestion: undefined })
      } finally {
        setIsLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current)
      }
    }
  }, [canTrigger, sectionType, textBeforeCursor, textAfterCursor, typedPrefix, caretPosition, terminologySystem, reasonForVisit, diagnosis])

  const applySuggestion = async (suggestion: AutocompleteSuggestion) => {
    if (!typedPrefix) return
    const beforeWithoutPrefix = textBeforeCursor.slice(0, textBeforeCursor.length - typedPrefix.length)
    const completed =
      suggestion.type === 'terminology'
        ? suggestion.code_refs?.[0]
          ? `${suggestion.full_text} (${suggestion.code_refs[0].system}:${suggestion.code_refs[0].code})`
          : suggestion.full_text
        : suggestion.full_text

    const insertStart = beforeWithoutPrefix.length
    const insertEnd = insertStart + completed.length
    const newValue = `${beforeWithoutPrefix}${completed}${textAfterCursor}`
    
    // Create highlight for the inserted text
    const newHighlight: NoteHighlight = {
      start: insertStart,
      end: insertEnd,
      type: 'autocomplete',
      suggestionType: suggestion.type === 'llm_rewrite' || suggestion.type === 'llm_summarize' ? undefined : suggestion.type,
      insertedAt: new Date().toISOString(),
    }
    
    // Adjust existing highlights that come after the insertion point
    const adjustedHighlights = highlights.map(h => {
      if (h.start >= insertStart) {
        // Shift highlights after insertion point
        const shift = completed.length - typedPrefix.length
        return { ...h, start: h.start + shift, end: h.end + shift }
      }
      return h
    })
    
    const updatedHighlights = [...adjustedHighlights, newHighlight].sort((a, b) => a.start - b.start)
    setHighlights(updatedHighlights)
    onHighlightsChange?.(updatedHighlights)
    
    // Update previousValueRef BEFORE onChange to prevent useEffect from processing this as a user edit
    previousValueRef.current = newValue
    onChange(newValue)

    // Increment usage count for custom phrases
    if (suggestion.custom_phrase_id) {
      try {
        await customPhraseService.incrementUsage(suggestion.custom_phrase_id)
      } catch (error) {
        console.error('Failed to increment phrase usage:', error)
        // Don't block the user if this fails
      }
    }

    // Clear suggestions immediately and prevent re-triggering
    setSuggestions([])
    setInlineState({ active: false, suggestion: undefined })
    setSelectedIndex(0)
    justAppliedSuggestionRef.current = true

    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (!el) return
      const newPos = insertEnd
      el.focus()
      el.setSelectionRange(newPos, newPos)
    })
  }
  
  // Update highlights when text changes (handle deletions, insertions, etc.)
  useEffect(() => {
    const prevValue = previousValueRef.current
    if (prevValue === value) return
    
    // Calculate the change
    let changeStart = 0
    
    // Find where the change occurred
    while (changeStart < prevValue.length && changeStart < value.length && prevValue[changeStart] === value[changeStart]) {
      changeStart++
    }
    
    let prevEnd = prevValue.length
    let newEndPos = value.length
    while (prevEnd > changeStart && newEndPos > changeStart && prevValue[prevEnd - 1] === value[newEndPos - 1]) {
      prevEnd--
      newEndPos--
    }
    
    const deletedLength = prevEnd - changeStart
    const insertedLength = newEndPos - changeStart
    const netChange = insertedLength - deletedLength
    
    // Get current highlights (use state getter to avoid dependency issues)
    setHighlights(currentHighlights => {
      // Update highlights based on the change
      const updatedHighlights = currentHighlights
        .map(h => {
          // If highlight ends at or before the change start, keep it unchanged
          // (change is happening after the highlight)
          if (h.end <= changeStart) {
            return h
          }
          // If highlight starts at or after the change end, shift it by the net change
          // (change is happening before the highlight)
          if (h.start >= prevEnd) {
            return { ...h, start: h.start + netChange, end: h.end + netChange }
          }
          // Otherwise, the change overlaps with the highlight
          // User is editing the highlighted text, so remove the highlight
          return null
        })
        .filter((h): h is NoteHighlight => h !== null)
        .sort((a, b) => a.start - b.start)
      
      onHighlightsChange?.(updatedHighlights)
      return updatedHighlights
    })
    
    previousValueRef.current = value
  }, [value, onHighlightsChange]) // Only depend on value to avoid loops
  
  // Sync with external highlights prop when value hasn't changed
  useEffect(() => {
    if (JSON.stringify(initialHighlights) !== JSON.stringify(highlights)) {
      // Only sync if the value hasn't changed (loading from saved consultation)
      if (previousValueRef.current === value) {
        setHighlights(initialHighlights)
      }
    }
  }, [initialHighlights, value, highlights])

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    if (event.key === 'Tab' && inlineState.active && inlineState.suggestion) {
      event.preventDefault()
      applySuggestion(inlineState.suggestion)
      return
    }

    if (suggestions.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setSelectedIndex((prev) => (prev + 1) % suggestions.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length)
    } else if ((event.key === 'Enter' || event.key === 'Tab') && selectedIndex >= 0) {
      if (event.key === 'Tab') {
        event.preventDefault()
      }
      const suggestion = suggestions[selectedIndex]
      if (suggestion) {
        event.preventDefault()
        applySuggestion(suggestion)
      }
    } else if (event.key === 'Escape') {
      setSuggestions([])
      setInlineState({ active: false, suggestion: undefined })
    }
  }

  const inlineGhostText = useMemo(() => {
    if (!inlineState.active || !inlineState.suggestion) return ''
    if (!typedPrefix) return ''
    if (!inlineState.suggestion.preview_text.toLowerCase().startsWith(typedPrefix.toLowerCase())) {
      return ''
    }
    return inlineState.suggestion.preview_text.slice(typedPrefix.length)
  }, [inlineState, typedPrefix])
  
  // Render text with highlights
  const renderTextWithHighlights = useMemo(() => {
    if (highlights.length === 0) {
      return (
        <span className="note-editor-text">
          {textBeforeCursor}
          <span className="note-editor-inline-prefix">{typedPrefix}</span>
          {inlineGhostText && <span className="note-editor-inline-ghost">{inlineGhostText}</span>}
          {textAfterCursor}
        </span>
      )
    }
    
    // Sort highlights by start position
    const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start)
    
    // Build segments with highlights
    const segments: Array<{ text: string; isHighlight: boolean }> = []
    let currentPos = 0
    
    sortedHighlights.forEach((highlight) => {
      // Add text before highlight
      if (highlight.start > currentPos) {
        const beforeText = value.slice(currentPos, highlight.start)
        if (beforeText) {
          segments.push({ text: beforeText, isHighlight: false })
        }
      }
      
      // Add highlighted text
      const highlightText = value.slice(highlight.start, highlight.end)
      if (highlightText) {
        segments.push({ text: highlightText, isHighlight: true })
      }
      
      currentPos = Math.max(currentPos, highlight.end)
    })
    
    // Add remaining text after last highlight
    if (currentPos < value.length) {
      const afterText = value.slice(currentPos)
      if (afterText) {
        segments.push({ text: afterText, isHighlight: false })
      }
    }
    
    // If no segments were created, just show the value
    if (segments.length === 0) {
      segments.push({ text: value, isHighlight: false })
    }
    
    return (
      <span className="note-editor-text">
        {segments.map((segment, idx) => 
          segment.isHighlight ? (
            <span key={idx} className="note-editor-highlight">
              {segment.text}
            </span>
          ) : (
            <span key={idx}>{segment.text}</span>
          )
        )}
        {inlineGhostText && <span className="note-editor-inline-ghost">{inlineGhostText}</span>}
      </span>
    )
  }, [value, highlights, textBeforeCursor, typedPrefix, inlineGhostText, textAfterCursor])

  return (
    <div className="note-editor-container">
      <div className="note-editor-wrapper">
        <div className="note-editor-overlay">
          {renderTextWithHighlights}
        </div>
        {selectedText && (
          <div ref={selectionPopupRef} className="note-editor-selection-popup">
            <button
              className="note-editor-save-phrase-btn"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleSaveAsPhrase()
              }}
              title="Save as custom phrase"
            >
              💾 Save as phrase
            </button>
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="note-editor-input"
          value={value}
          onChange={(e) => {
            onChange(e.target.value)
            // Update caret position after onChange
            requestAnimationFrame(() => {
              handleSelectionChange()
            })
          }}
          onSelect={handleSelectionChange}
          onKeyUp={handleSelectionChange}
          onKeyDown={handleKeyDown}
          onClick={handleSelectionChange}
          spellCheck={false}
        />
      </div>
      {showSavePhraseModal && (
        <SavePhraseModal
          selectedText={selectedText}
          defaultTrigger={savePhraseTrigger}
          sectionType={sectionType}
          onSave={handleSavePhrase}
          onCancel={() => {
            setShowSavePhraseModal(false)
            setSelectedText('')
          }}
        />
      )}
      <div className="note-editor-suggestions">
        <div className="note-editor-suggestions-header">
          <span className="note-editor-suggestions-title">Smart suggestions</span>
          <span className="note-editor-suggestions-hint">Tab/Enter to accept, Esc to dismiss</span>
          {isLoading && <span className="note-editor-suggestions-status">Loading...</span>}
        </div>
        {suggestions.length === 0 ? (
          <div className="note-editor-suggestions-empty">
            <span>Suggestions will appear here as you type.</span>
          </div>
        ) : (
          <ul className="note-editor-suggestions-list">
            {suggestions.map((s, index) => {
              // For phrases and templates, show the full text preview
              // For terminology, show the full text with code
              const showFullText = s.type === 'phrase' || s.type === 'template'
              const displayText = showFullText 
                ? s.full_text 
                : s.type === 'terminology'
                ? `${s.full_text}${s.code_refs?.[0] ? ` (${s.code_refs[0].system}:${s.code_refs[0].code})` : ''}`
                : s.preview_text
              
              // Truncate long text but show enough to be useful (around 120 chars)
              const maxLength = 120
              const truncatedText = displayText.length > maxLength 
                ? displayText.slice(0, maxLength) + '...'
                : displayText
              
              return (
                <li
                  key={`${s.type}-${index}-${s.preview_text}`}
                  className={`note-editor-suggestion-item ${index === selectedIndex ? 'is-selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    applySuggestion(s)
                  }}
                >
                  <div className="note-editor-suggestion-content">
                    <span className="note-editor-suggestion-type">{s.type}</span>
                    {s.preview_text !== s.full_text && (
                      <span className="note-editor-suggestion-trigger">{s.preview_text}</span>
                    )}
                    <span className="note-editor-suggestion-text">{truncatedText}</span>
                  </div>
                  {s.custom_phrase_id && (
                    <button
                      className="note-editor-suggestion-delete"
                      onClick={(e) => handleDeleteCustomPhrase(s.custom_phrase_id!, e)}
                      title="Delete custom phrase"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      ×
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

interface SavePhraseModalProps {
  selectedText: string
  defaultTrigger: string
  sectionType?: NoteSectionType
  onSave: (trigger: string) => void
  onCancel: () => void
}

function SavePhraseModal({ selectedText, defaultTrigger, onSave, onCancel }: SavePhraseModalProps) {
  const [trigger, setTrigger] = useState(defaultTrigger)
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    if (!trigger.trim()) {
      alert('Please enter a trigger phrase')
      return
    }
    setIsSaving(true)
    try {
      await onSave(trigger)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="note-editor-modal-overlay" onClick={onCancel}>
      <div className="note-editor-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Save as Custom Phrase</h3>
        <div className="note-editor-modal-content">
          <div className="note-editor-modal-field">
            <label htmlFor="phrase-text">Phrase text:</label>
            <textarea
              id="phrase-text"
              value={selectedText}
              readOnly
              rows={4}
              className="note-editor-modal-textarea"
            />
          </div>
          <div className="note-editor-modal-field">
            <label htmlFor="phrase-trigger">
              Trigger phrase <span className="note-editor-modal-hint">(what to type to get this suggestion)</span>:
            </label>
            <input
              id="phrase-trigger"
              type="text"
              value={trigger}
              onChange={(e) => setTrigger(e.target.value)}
              placeholder="e.g., normal cv, safety net, follow up"
              className="note-editor-modal-input"
              autoFocus
            />
            <p className="note-editor-modal-hint">
              When you type this trigger phrase, the full text will be suggested.
            </p>
          </div>
        </div>
        <div className="note-editor-modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={isSaving || !trigger.trim()}>
            {isSaving ? 'Saving...' : 'Save Phrase'}
          </button>
        </div>
      </div>
    </div>
  )
}

